// prettier-ignore
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");
		this.calcHeight();
		this.setupEventListeners();
	}

	calcHeight() {
		this.results = document.querySelector(".predictive-search__result-group");
		this.resultsTabs = document.querySelector(
			".predictive-search__result-tabs",
		);

		if (this.results && this.resultsTabs) {
			this.results.style.setProperty(
				"--search-height",
				`${parseInt(this.resultsTabs.getBoundingClientRect().bottom)}px`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	normalizeSearchText(text) {
		return (text || "")
			.toLocaleLowerCase("tr-TR")
			.replace(/[^0-9a-zçğıöşü\s]/gi, " ")
			.replace(/\s+/g, " ")
			.trim();
	}

	getCompletedQueryFromPredictiveProducts() {
		const currentQuery = this.normalizeSearchText(this.getQuery());

		if (!currentQuery) return null;

		const queryWords = currentQuery.split(" ").filter(Boolean);

		if (!queryWords.length) return null;

		let productElements = Array.from(
			this.querySelectorAll('[data-predictive-search] a[href*="/products/"]'),
		);

		if (!productElements.length) {
			productElements = Array.from(
				this.querySelectorAll(
					'[data-predictive-search] .predictive-search__item, [data-predictive-search] .card-wrapper',
				),
			);
		}

		const uniqueProductElements = [...new Set(productElements)];

		for (const productElement of uniqueProductElements) {
			const productText = this.normalizeSearchText(productElement.textContent);
			const productWords = productText.split(" ").filter(Boolean);

			if (productWords.length < queryWords.length) continue;

			for (
				let startIndex = 0;
				startIndex <= productWords.length - queryWords.length;
				startIndex++
			) {
				let isMatch = true;

				for (let wordIndex = 0; wordIndex < queryWords.length; wordIndex++) {
					const queryWord = queryWords[wordIndex];
					const productWord = productWords[startIndex + wordIndex];
					const isLastWord = wordIndex === queryWords.length - 1;

					if (isLastWord) {
						if (!productWord.startsWith(queryWord)) {
							isMatch = false;
							break;
						}
					} else if (productWord !== queryWord) {
						isMatch = false;
						break;
					}
				}

				if (isMatch) {
					return productWords
						.slice(startIndex, startIndex + queryWords.length)
						.join(" ");
				}
			}
		}

		return null;
	}

	completeSearchQueryBeforeSubmit() {
		const completedQuery = this.getCompletedQueryFromPredictiveProducts();
		const currentQuery = this.normalizeSearchText(this.getQuery());

		if (completedQuery && completedQuery !== currentQuery) {
			this.input.value = completedQuery;
		}
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		const selectedOption = this.querySelector('[aria-selected="true"] a');
		const isCardLink = event.submitter?.classList?.contains("card__link");

		if (!this.getQuery().length || selectedOption || isCardLink) {
			event.preventDefault();
			return;
		}

		this.completeSearchQueryBeforeSubmit();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close(true);
			});
		}
	}

	onBlur() {
		if (this.headerInput) {
			this.headerInput.blur();
		}
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);

		switch (event.code) {
			case "Enter":
				if (
					this.querySelector(
						'[aria-selected="true"] a, button[aria-selected="true"]',
					)
				) {
					event.preventDefault();
					this.selectOption();
				}
				break;

			case "Escape":
				event.preventDefault();
				this.close(true);
				this.closeResults(true);

				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				previousTerm &&
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				return;
			}
			const newButtonText = previousTerm
				? currentButtonText.replace(previousTerm, newTerm)
				: currentButtonText;
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		if (/^\d{8,14}$/.test(searchTerm)) {
			fetch(`/search?q=${encodeURIComponent(searchTerm)}&section_id=main-search`)
				.then((r) => r.text())
				.then((html) => {
					const doc = new DOMParser().parseFromString(html, "text/html");
					const productList = doc.querySelector(".collection-product-list");

					if (!productList) {
						this.close();
						return;
					}

					this.predictiveSearchResults.innerHTML = `
<div id="predictive-search-results">
	<div id="predictive-search-results-groups-wrapper">
		<div class="predictive-search__result-group">
			${productList.outerHTML}
		</div>
	</div>
</div>`;

					this.open();
				});

			return;
		}

		const isBarcode = /^\d{8,14}$/.test(searchTerm);

		if (isBarcode) {
			fetch(`/search?q=${encodeURIComponent(searchTerm)}&view=ajax`)
				.then((r) => r.json())
				.then((data) => {
					if (!data.results.length) {
						this.close();
						return;
					}

					const html = `
<div id="predictive-search-results">
	<div id="predictive-search-results-groups-wrapper">
		<div class="predictive-search__result-group">
			<div class="predictive-search-results-wrapper">
				<ul class="predictive-search__results-list active">
					${data.results.map(product=>`
						<li class="predictive-search__list-item">
							<a href="${product.url}" class="predictive-search__item">
								<img
									src="${product.image}"
									width="60"
									height="60"
									style="object-fit:cover;border-radius:6px;"
								>
								<div style="margin-left:12px">
									<div>${product.title}</div>
									<div>${product.price}</div>
								</div>
							</a>
						</li>
					`).join("")}
				</ul>
			</div>
		</div>
	</div>
</div>
`;

					this.renderSearchResults(html);
				});

			return;
		}

		const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			this.calcHeight();
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
			{ signal: this.abortController.signal },
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;

				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);

				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				this.calcHeight();

				try {
					colorSwatches();
				} catch (err) {}
			})
			.catch((error) => {
				if (error?.code === 20) {
					return;
				}
				this.close();
				throw error;
			});

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		if (!this.statusElement) return;

		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		const firstTab = this.querySelector(".predictive-search__result-tab");

		if (results.length != 0 && firstTab) {
			results[0].classList.add("active");
			firstTab.classList.add("active");

			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
			});
		}
	}

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");

		const resultCountElement = this.querySelector(
			"[data-predictive-search-live-region-count-value]",
		);

		if (resultCountElement) {
			this.setLiveRegionText(resultCountElement.textContent);
		}
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);

		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.removeAttribute("style");
		}

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);